<template>
  <div ref="index" class="container">
    <div v-if="!isTicket" class="skip" @click="skip">
      {{ $t('page.feedback.skip') }}
    </div>
    <div class="main" :class="{ ticket: isTicket }">
      <div v-if="!isTicket" class="ended">
        {{ isChatMode ? $t('page.feedback.chat_ended') : $t('page.feedback.voice_ended') }}
      </div>
      <div v-if="submittedFeedback" class="experience">Thank you for your feedback!</div>
      <div v-else class="experience">
        {{ $t('page.feedback.experience') }}
      </div>
      <template v-if="!submittedFeedback">
        <div class="rating">
          <div v-for="i in 5" :key="i" class="star-container">
            <img v-if="i <= selectedStar" src="@/assets/img/star-clicked.svg" @click="handleSelectStar(i)" />
            <img v-else src="@/assets/img/star-unclicked.svg" @click="handleSelectStar(i)" />
            <span v-if="i === 1" class="star-label">{{ $t('page.feedback.not_helpful') }}</span>
            <span v-if="i === 5" class="star-label">{{ $t('page.feedback.helpful') }}</span>
          </div>
        </div>
        <!-- TODO: Update to "New version" -->
        <!-- <div class="rating">
          <div
            v-for="index in 5"
            :key="index"
            class="rate-container"
          >
            <img
              class="rate-image"
              :src="getImagePath(index)"
              :alt="`Rating image ${index}`"
              @mouseenter="handleMouseEnter(index)"
              @mouseleave="handleMouseLeave"
              @click="handleSelectStar(index)"
            >
            <span class="rate-text">{{ Locale.i18n.t(RATING_IMAGES[index - 1].text) }}</span>
          </div>
        </div> -->

        <!-- TODO: Update to !== null in "New Version" -->
        <template v-if="selectedStar > 0">
          <!-- 1-3 -->
          <template v-if="selectedStar < 4">
            <div class="question">
              {{ $t('page.feedback.improved') }}
            </div>
            <div class="feedbacks">
              <div
                v-for="f in feedbackCategories"
                :key="f.feedbackSelectionId"
                :class="{ selected: selectedOptionIds.includes(f.feedbackSelectionId) }"
                @click="toggleOption(f.feedbackSelectionId)"
              >
                {{ f.name }}
              </div>
            </div>
            <textarea v-model="comment" rows="5" :placeholder="$t('page.feedback.comments')" />
          </template>
          <!-- 4-5 -->
          <textarea v-else v-model="comment" rows="5" :placeholder="$t('page.feedback.comments')" />
        </template>
      </template>
    </div>
    <div v-if="selectedStar && !submittedFeedback" :class="['submit']" @click="handleSubmit">
      {{ $t('page.feedback.submit') }}
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { FeedbackCategory } from '@/model/pleased';
import { Bridge } from '@/common/bridge';
import { GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { mixins } from 'vue-class-component';
import * as PleasedApi from '@/common/api/api.pleased';
import * as Locale from '@/plugins/i18n';
import { analytics, syncLogs, COMPONENT_EVENTS } from '@/common/logs';
import { ChatRateMode } from '@/common/api/models.pleased';

const RATING_IMAGES = [
  {
    text: 'page.feedback.rating.unhappy',
    png: require('@/assets/img/rating/unhappy.png'),
    gif: require('@/assets/img/rating/unhappy.gif'),
  },
  {
    text: 'page.feedback.rating.unsatisfied',
    png: require('@/assets/img/rating/unsatisfied.png'),
    gif: require('@/assets/img/rating/unsatisfied.gif'),
  },
  {
    text: 'page.feedback.rating.neutral',
    png: require('@/assets/img/rating/neutral.png'),
    gif: require('@/assets/img/rating/neutral.gif'),
  },
  {
    text: 'page.feedback.rating.satisfied',
    png: require('@/assets/img/rating/satisfied.png'),
    gif: require('@/assets/img/rating/satisfied.gif'),
  },
  {
    text: 'page.feedback.rating.happy',
    png: require('@/assets/img/rating/happy.png'),
    gif: require('@/assets/img/rating/happy.gif'),
  },
];

@Component
export default class ChatRating extends mixins(LogPageEventMixin) {
  declare $refs: Vue['$refs'] & {
    index: HTMLDivElement;
  };
  @Prop({ default: [] }) readonly feedbackCategories!: FeedbackCategory[];
  @Prop({ default: 'chat' }) readonly mode!: ChatRateMode;
  @Prop(String) readonly entityId!: string;
  @Prop({ type: String, default: '' }) readonly trackEventContext!: string;
  @Prop({ type: Boolean, default: false }) readonly canConnectAgent!: boolean;

  readonly RATING_IMAGES = RATING_IMAGES;
  readonly Locale = Locale;

  get [PAGE_TRACKING]() {
    return {
      name: `${this.trackEventContext}${PAGE_EVENTS.CSAT}`,
      selection: this.entityId,
      metadata: {
        type: this.mode,
        id: this.entityId,
        ...(this.mode === 'AI' ? { sessionId: analytics.sessionId } : {}),
      },
    };
  }

  selectedStar: number | null = 0; // TODO: Update to null in "New version"
  hoveredStar: number | null = null;

  selectedOptionIds: number[] = [];
  comment = '';
  submittedFeedback = false;

  get isTicket() {
    return this.mode === 'ticket';
  }

  get isChatMode() {
    return this.mode === 'chat' || this.mode === 'AI';
  }

  getImagePath(index: number) {
    const isSelected = this.selectedStar === index;
    const isHovered = this.hoveredStar === index;
    const isGifFormat = !this.selectedStar || isSelected || isHovered;

    const format = isGifFormat ? 'gif' : 'png';

    return this.RATING_IMAGES[index - 1][format];
  }

  created() {
    this.trackSeenRating();
  }

  handleMouseEnter(i: number) {
    this.hoveredStar = i;
  }

  handleMouseLeave() {
    this.hoveredStar = null;
  }

  handleSelectStar(i: number) {
    this.selectedStar = i;

    analytics.trackClick({
      name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_CLICKED}`,
      selection: this.entityId,
      metadata: { rating: i, type: this.mode, UI: 'old' },
    });

    this.$emit('send:feedback', {
      rating: this.selectedStar,
      comment: this.comment,
      feedbackSelectionIds: this.selectedStar < 4 ? this.selectedOptionIds : [],
      type: this.mode,
    });
  }

  toggleOption(id: number) {
    const index = this.selectedOptionIds.indexOf(id);

    analytics.trackClick({
      name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_FEEDBACK_OPTION}`,
      selection: this.entityId,
      metadata: {
        feedbackOption: id,
        title: this.feedbackCategories.find((f) => f.feedbackSelectionId === id)?.name,
        selected: index === -1,
        type: this.mode,
      },
    });

    if (index !== -1) {
      this.selectedOptionIds.splice(index, 1);
    } else {
      this.selectedOptionIds.push(id);
    }
  }

  async skip() {
    this.$gtag.event(GA_EVENT_NAME_ENUM.CHAT_RATING_SKIP);
    analytics.trackClick({
      name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_SKIP}`,
      selection: this.entityId,
      metadata: { type: this.mode },
    });

    await syncLogs();
    Bridge.sendToParent('CLEAR_FLOAT');
    Bridge.sendToParent('CLOSE');
  }

  handleSubmit() {
    this.$gtag.event(GA_EVENT_NAME_ENUM.CHAT_RATING_SUBMIT);

    analytics.trackClick({
      name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_SUBMITTED}`,
      selection: this.entityId,
      metadata: { type: this.mode },
    });

    const showConnectSupport = this.canConnectAgent && this.mode === 'AI' && Number(this.selectedStar) <= 2;

    this.$emit('submit', {
      rating: this.selectedStar,
      comment: this.comment,
      feedbackSelectionIds: this.selectedStar! < 4 ? this.selectedOptionIds : [],
      type: this.mode,
      showedConnectSupport: showConnectSupport,
    });

    // For AI CSAT we propose user to connect with support team if rating is less than 3
    if (showConnectSupport) {
      this.openConnectSupportModal();
    }

    this.submittedFeedback = true;
  }

  trackSeenRating() {
    // Do not track AI mode for now
    if (this.mode === 'AI') return;
    this.$gtag.event(GA_EVENT_NAME_ENUM.CHAT_RATING_SHOW);
    PleasedApi.trackEntitySeen({ entityType: this.mode, entityId: parseInt(this.entityId) });
  }

  openConnectSupportModal() {
    if (!this.canConnectAgent) return;

    analytics.track({
      name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_LOW_RATING_SHOWN}`,
      selection: this.entityId,
      metadata: { type: this.mode },
    });

    const dialog = this.$dialog({
      content: this.$t('page.feedback.connect_support_dialog.text') as string,
      buttons: [
        {
          name: this.$t('global.yes'),
          callback: () => {
            analytics.trackClick({
              name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_LOW_RATING_CONNECT_SUPPORT}`,
              selection: this.entityId,
              metadata: { type: this.mode },
            });

            this.$emit('connect-support');
            dialog.close();
          },
        },
        {
          name: this.$t('global.no'),
          callback: async () => {
            analytics.trackClick({
              name: `${this.trackEventContext}${COMPONENT_EVENTS.CSAT_LOW_RATING_CLOSE}`,
              selection: this.entityId,
              metadata: { type: this.mode },
            });

            await syncLogs();
            Bridge.sendToParent('CLEAR_FLOAT');
            Bridge.sendToParent('CLOSE');
          },
        },
      ],
    });
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variable.scss';
.container {
  display: flex;
  flex-direction: column;
  font-size: 1.4rem;
  color: $chat-rating-text-color;
  padding: 2.8rem 2.8rem 0;
  position: relative;
  flex: 1;
  background: $chat-rating-background;

  .skip {
    align-self: flex-end;
    margin-right: -0.7rem;
    color: $chat-rating-skip-text-color;
    text-decoration: underline;
    font-weight: bold;
  }
  .main {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 3.2rem;

    &.ticket {
      padding-top: 0;
    }

    .ended {
      font-style: italic;
      margin-bottom: 2rem;
      align-self: center;
    }
    .experience {
      font-size: 16px;
      margin-bottom: 2rem;
      font-weight: bold;
    }
    .rating {
      display: flex;
      width: 100%;
      justify-content: space-between;
      margin-bottom: 2.8rem;
      gap: 8px;
    }
    .star-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    }
    .star-label {
      font-size: 11px;
      text-align: center;
    }
    .rate-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;

      span {
        font-size: 11px;
      }
    }
    .rate-image {
      width: 4.8rem;
      height: 4.8rem;
      transition: opacity 0.2s ease;
    }
    .rate-text {
      text-align: center;
    }
    .question {
      margin-bottom: 1.6rem;
    }
    .rate-image:hover {
      cursor: pointer;
      opacity: 0.5;
    }
    .feedbacks {
      display: flex;
      flex-wrap: wrap;
      width: 100%;
      color: $outline-button-text-color;
      background: $outline-button-background;
      div {
        flex-basis: 48%;
        height: 4.4rem;
        margin: 0 0 1.6rem 0;
        text-align: center;
        &:nth-child(2n + 1) {
          margin-right: 1rem;
        }

        cursor: pointer;
        border-radius: 0.8rem;
        border: solid 0.1rem $outline-button-outline-color;
        display: flex;
        justify-content: center;
        align-items: center;
        &.selected {
          background-color: $filled-button-background;
          color: $filled-button-text-color;
        }
      }
    }
    textarea {
      width: 100%;
      outline: none;
      height: 15.2rem;
      padding: 1.2rem 0.9rem;
      border-radius: 0.2rem;
      border: solid 0.1rem $chat-rating-comment-input-border-color;
      color: $chat-rating-comment-input-text-color;
      outline: none;
      transition: border-color 0.2s ease;
      resize: none;
      background: $chat-rating-comment-input-background;
      &::placeholder {
        color: $chat-rating-comment-input-placeholder-color;
      }
    }
    .thank {
      align-self: center;
      font-size: 2rem;
      margin-top: 3.5rem;
      font-weight: bold;
    }
  }

  .submit {
    border-radius: 4px;
    padding: 1.4rem 0;
    display: flex;
    justify-content: center;
    font-size: 1.4rem;
    margin: 2rem 0;
    background-color: $filled-button-background;
    color: $filled-button-text-color;
    cursor: pointer;
  }
}
</style>
